import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DevelopmentRecipe, DevelopmentStep } from '@/types/develop';
import { useTranslation } from '@/hooks/useTranslation';
import { v4 as uuidv4 } from 'uuid';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MobileStepEditor } from './MobileStepEditor';
import { useMobileStepEditor } from '@/hooks/useMobileStepEditor';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from 'next-themes';
import { toast } from '@/hooks/use-toast';

// Default starter steps for new recipes
const defaultSteps: DevelopmentStep[] = [{
  id: uuidv4(),
  name: 'Pre-Soak',
  time: 60,
  // 1 minute
  agitationType: 'none',
  agitationDuration: 0
}, {
  id: uuidv4(),
  name: 'Developer',
  time: 600,
  // 10 minutes
  agitationType: 'intermittent',
  agitationInterval: 60,
  // 1 minute
  agitationDuration: 10 // 10 seconds
}, {
  id: uuidv4(),
  name: 'Stop Bath',
  time: 60,
  // 1 minute
  agitationType: 'continuous',
  agitationDuration: 60 // all the time
}, {
  id: uuidv4(),
  name: 'Fixer',
  time: 300,
  // 5 minutes
  agitationType: 'intermittent',
  agitationInterval: 60,
  // 1 minute
  agitationDuration: 10 // 10 seconds
}, {
  id: uuidv4(),
  name: 'Fixer Remover',
  time: 120,
  // 2 minutes
  agitationType: 'continuous',
  agitationDuration: 120 // entire time
}, {
  id: uuidv4(),
  name: 'Wash',
  time: 600,
  // 10 minutes
  agitationType: 'intermittent',
  agitationInterval: 180,
  // 3 minutes
  agitationDuration: 30 // 30 seconds
}, {
  id: uuidv4(),
  name: 'Photo Flo',
  time: 60,
  // 1 minute
  agitationType: 'continuous',
  agitationDuration: 60 // entire time
}];
interface RecipeEditorProps {
  recipe?: DevelopmentRecipe;
  onSave: (recipe: Omit<DevelopmentRecipe, 'id' | 'createdAt' | 'updatedAt' | 'isUserCreated'>) => void;
  onCancel: () => void;
  darkMode?: boolean;
}
export const RecipeEditor: React.FC<RecipeEditorProps> = ({
  recipe,
  onSave,
  onCancel,
  darkMode = false
}) => {
  const {
    t
  } = useTranslation();
  const isEditing = !!recipe;
  const {
    theme,
    systemTheme
  } = useTheme();
  const isDark = theme === 'dark' || theme === 'system' && systemTheme === 'dark';
  const isDarkroomMode = darkMode;

  // Form state
  const [name, setName] = useState(recipe?.name || '');
  const [filmStock, setFilmStock] = useState(recipe?.filmStock || '');
  const [iso, setIso] = useState(recipe?.iso?.toString() || '');
  const [developer, setDeveloper] = useState(recipe?.developer || '');
  const [temperature, setTemperature] = useState(recipe?.temperature?.toString() || '20');
  const [pushPull, setPushPull] = useState<'standard' | 'push' | 'pull'>(recipe?.pushPull || 'standard');
  const [pushPullStops, setPushPullStops] = useState(recipe?.pushPullStops?.toString() || '0');
  const [notes, setNotes] = useState(recipe?.notes || '');
  const [steps, setSteps] = useState<DevelopmentStep[]>(recipe?.steps || defaultSteps);

  // Input validation errors
  const [nameError, setNameError] = useState<string | null>(null);
  const [filmStockError, setFilmStockError] = useState<string | null>(null);
  const [isoError, setIsoError] = useState<string | null>(null);
  const [developerError, setDeveloperError] = useState<string | null>(null);
  const [temperatureError, setTemperatureError] = useState<string | null>(null);
  const [pushPullStopsError, setPushPullStopsError] = useState<string | null>(null);
  const [notesError, setNotesError] = useState<string | null>(null);

  // Character limits
  const NAME_MAX_LENGTH = 40;
  const FILM_STOCK_MAX_LENGTH = 20;
  const DEVELOPER_MAX_LENGTH = 20;
  const ISO_MAX_DIGITS = 4;
  const TEMPERATURE_MAX_DIGITS = 2;
  const NOTES_MAX_LENGTH = 100;
  const [editingStepIndex, setEditingStepIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();
  const {
    isEditorOpen,
    setIsEditorOpen,
    currentStep,
    currentStepIndex,
    isNewStep,
    openStepEditor,
    openNewStepEditor,
    handleSaveStep,
    handleDeleteStep,
    handleMoveStepUp,
    handleMoveStepDown,
    handleEditorMoveStepUp,
    handleEditorMoveStepDown
  } = useMobileStepEditor(steps, setSteps);

  // Validation functions
  const validateName = (value: string): boolean => {
    if (!value.trim()) {
      setNameError('Recipe name is required');
      return false;
    }
    if (value.length > NAME_MAX_LENGTH) {
      setNameError(`Recipe name cannot exceed ${NAME_MAX_LENGTH} characters`);
      return false;
    }
    setNameError(null);
    return true;
  };
  const validateFilmStock = (value: string): boolean => {
    if (value && value.length > FILM_STOCK_MAX_LENGTH) {
      setFilmStockError(`Film stock cannot exceed ${FILM_STOCK_MAX_LENGTH} characters`);
      return false;
    }
    setFilmStockError(null);
    return true;
  };
  const validateIso = (value: string): boolean => {
    if (!value) {
      setIsoError(null);
      return true;
    }
    const isoNum = Number(value);
    if (isNaN(isoNum)) {
      setIsoError('ISO must be a number');
      return false;
    }
    if (isoNum < 1) {
      setIsoError('ISO must be positive');
      return false;
    }
    if (value.length > ISO_MAX_DIGITS) {
      setIsoError(`ISO cannot exceed ${ISO_MAX_DIGITS} digits`);
      return false;
    }
    setIsoError(null);
    return true;
  };
  const validateDeveloper = (value: string): boolean => {
    if (value && value.length > DEVELOPER_MAX_LENGTH) {
      setDeveloperError(`Developer cannot exceed ${DEVELOPER_MAX_LENGTH} characters`);
      return false;
    }
    setDeveloperError(null);
    return true;
  };
  const validateTemperature = (value: string): boolean => {
    if (!value) {
      setTemperatureError(null);
      return true;
    }
    const tempNum = Number(value);
    if (isNaN(tempNum)) {
      setTemperatureError('Temperature must be a number');
      return false;
    }
    if (tempNum < 0 || tempNum > 99) {
      setTemperatureError('Temperature must be between 0-99°C');
      return false;
    }
    if (value.length > TEMPERATURE_MAX_DIGITS) {
      setTemperatureError(`Temperature cannot exceed ${TEMPERATURE_MAX_DIGITS} digits`);
      return false;
    }
    setTemperatureError(null);
    return true;
  };
  const validatePushPullStops = (value: string): boolean => {
    if (pushPull === 'standard') {
      setPushPullStopsError(null);
      return true;
    }
    if (!value) {
      setPushPullStopsError('Stops value is required');
      return false;
    }
    const stopsNum = Number(value);
    if (isNaN(stopsNum)) {
      setPushPullStopsError('Stops must be a number');
      return false;
    }
    if (stopsNum < 0 || stopsNum > 5) {
      setPushPullStopsError('Stops must be between 0-5');
      return false;
    }
    setPushPullStopsError(null);
    return true;
  };
  const validateNotes = (value: string): boolean => {
    if (!value) {
      setNotesError(null);
      return true;
    }
    if (value.length > NOTES_MAX_LENGTH) {
      setNotesError(`Notes cannot exceed ${NOTES_MAX_LENGTH} characters`);
      return false;
    }
    setNotesError(null);
    return true;
  };
  const handleSave = () => {
    // Validate all inputs before saving
    const isNameValid = validateName(name);
    const isFilmStockValid = validateFilmStock(filmStock);
    const isIsoValid = validateIso(iso);
    const isDeveloperValid = validateDeveloper(developer);
    const isTemperatureValid = validateTemperature(temperature);
    const isPushPullStopsValid = validatePushPullStops(pushPullStops);
    const isNotesValid = validateNotes(notes);
    if (steps.length === 0) {
      toast({
        title: "No steps added",
        description: "Please add at least one development step",
        variant: "destructive"
      });
      return;
    }
    if (isNameValid && isFilmStockValid && isIsoValid && isDeveloperValid && isTemperatureValid && isPushPullStopsValid && isNotesValid) {
      onSave({
        name,
        filmStock: filmStock || undefined,
        iso: iso ? parseInt(iso, 10) : undefined,
        developer: developer || undefined,
        temperature: temperature ? parseInt(temperature, 10) : undefined,
        pushPull,
        pushPullStops: pushPullStops ? parseInt(pushPullStops, 10) : 0,
        notes: notes || undefined,
        steps
      });
    } else {
      toast({
        title: "Validation errors",
        description: "Please fix the highlighted errors before saving",
        variant: "destructive"
      });
    }
  };
  const addStep = () => {
    const newStep: DevelopmentStep = {
      id: uuidv4(),
      name: '',
      time: 60,
      agitationType: 'none'
    };
    setSteps([...steps, newStep]);
    setEditingStepIndex(steps.length);
  };
  const editStep = (index: number) => {
    setEditingStepIndex(index);
  };
  const deleteStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
    if (editingStepIndex === index) {
      setEditingStepIndex(null);
    } else if (editingStepIndex !== null && editingStepIndex > index) {
      setEditingStepIndex(editingStepIndex - 1);
    }
  };
  const moveStepUp = (index: number) => {
    if (index <= 0) return;
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index - 1];
    newSteps[index - 1] = temp;
    setSteps(newSteps);
    if (editingStepIndex === index) {
      setEditingStepIndex(index - 1);
    } else if (editingStepIndex === index - 1) {
      setEditingStepIndex(index);
    }
  };
  const moveStepDown = (index: number) => {
    if (index >= steps.length - 1) return;
    const newSteps = [...steps];
    const temp = newSteps[index];
    newSteps[index] = newSteps[index + 1];
    newSteps[index + 1] = temp;
    setSteps(newSteps);
    if (editingStepIndex === index) {
      setEditingStepIndex(index + 1);
    } else if (editingStepIndex === index + 1) {
      setEditingStepIndex(index);
    }
  };
  const updateStep = (index: number, updates: Partial<DevelopmentStep>) => {
    setSteps(steps.map((step, i) => i === index ? {
      ...step,
      ...updates
    } : step));
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Theme-based styling
  const getBaseStyles = () => {
    if (isDarkroomMode) {
      return {
        mainBg: "bg-black",
        cardBg: "bg-black border-red-900",
        headerBg: "bg-black border-red-900",
        text: "text-red-500 example-text",
        inputBg: "bg-black border-red-800",
        buttonBg: "bg-red-900 hover:bg-red-800",
        buttonText: "text-red-100",
        mutedText: "text-red-700 example-text",
        tableRowBg: "bg-black border-red-900",
        tableHeaderBg: "bg-black border-red-900 text-red-500",
        tableCellBg: "text-red-500 border-red-900",
        editRowBg: "bg-red-950 border-red-900",
        placeholderText: "placeholder:text-red-800"
      };
    } else if (isDark) {
      return {
        mainBg: "bg-[#121212]",
        cardBg: "bg-[#1e1e1e] border-[#333]",
        headerBg: "bg-[#1e1e1e] border-[#333]",
        text: "text-[#e0e0e0]",
        inputBg: "bg-[#2a2a2a] border-[#444]",
        buttonBg: "bg-[#2a2a2a] hover:bg-[#3a3a3a]",
        buttonText: "text-[#e0e0e0]",
        mutedText: "text-[#999]",
        tableRowBg: "bg-[#1e1e1e] border-[#333]",
        tableHeaderBg: "bg-[#1e1e1e] border-[#333] text-[#e0e0e0]",
        tableCellBg: "text-[#e0e0e0] border-[#333]",
        editRowBg: "bg-[#2a2a2a] border-[#333]",
        placeholderText: ""
      };
    } else {
      return {
        mainBg: "bg-background",
        cardBg: "bg-card border-border",
        headerBg: "bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200 shadow-sm",
        text: "text-foreground",
        inputBg: "bg-background border-input",
        buttonBg: "bg-primary hover:bg-primary/90",
        buttonText: "text-primary-foreground",
        mutedText: "text-muted-foreground",
        tableRowBg: "bg-card border-border",
        tableHeaderBg: "bg-card border-border",
        tableCellBg: "text-foreground border-border",
        editRowBg: "bg-muted border-border",
        placeholderText: ""
      };
    }
  };
  const styles = getBaseStyles();
  return <div className={cn("space-y-6", styles.mainBg)}>
      <div className={cn("flex justify-between items-center p-4 mb-2 rounded-none", styles.headerBg)}>
        <h2 className={cn("text-xl font-bold", styles.text)}>
          {isEditing ? t('Edit Recipe') : t('Create New Recipe')}
        </h2>
        
        <Button variant="ghost" onClick={onCancel} className={cn("transition-all duration-200 hover:scale-105", isDark && "hover:bg-[#2a2a2a]", isDarkroomMode && "text-red-500 hover:bg-red-950")}>
          {t('Cancel')}
        </Button>
      </div>
      
      <Card className={cn(styles.cardBg, "w-full shadow-md")}>
        <CardHeader>
          <CardTitle className={cn("text-lg", styles.text)}>
            Recipe Details
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className={styles.text}>
                Recipe Name (required) 
              </Label>
              <Input id="name" value={name} onChange={e => {
              setName(e.target.value);
              validateName(e.target.value);
            }} placeholder="e.g., HP5+ in D-76" className={cn(styles.inputBg, styles.placeholderText, nameError && 'border-destructive')} maxLength={NAME_MAX_LENGTH} onBlur={e => validateName(e.target.value)} required />
              {nameError && <p className="text-xs text-destructive">{nameError}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="developer" className={styles.text}>
                Developer 
              </Label>
              <Input id="developer" value={developer} onChange={e => {
              setDeveloper(e.target.value);
              validateDeveloper(e.target.value);
            }} placeholder="e.g., D-76, HC-110 (B)" className={cn(styles.inputBg, styles.placeholderText, developerError && 'border-destructive')} maxLength={DEVELOPER_MAX_LENGTH} onBlur={e => validateDeveloper(e.target.value)} />
              {developerError && <p className="text-xs text-destructive">{developerError}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="filmStock" className={styles.text}>
                Film Stock 
              </Label>
              <Input id="filmStock" value={filmStock} onChange={e => {
              setFilmStock(e.target.value);
              validateFilmStock(e.target.value);
            }} placeholder="e.g., Tri-X, HP5+" className={cn(styles.inputBg, styles.placeholderText, filmStockError && 'border-destructive')} maxLength={FILM_STOCK_MAX_LENGTH} onBlur={e => validateFilmStock(e.target.value)} />
              {filmStockError && <p className="text-xs text-destructive">{filmStockError}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="iso" className={styles.text}>
                ISO 
              </Label>
              <Input id="iso" type="number" value={iso} onChange={e => {
              setIso(e.target.value);
              validateIso(e.target.value);
            }} placeholder="e.g., 400" className={cn(styles.inputBg, styles.placeholderText, isoError && 'border-destructive')} onBlur={e => validateIso(e.target.value)} min="1" max="9999" />
              {isoError && <p className="text-xs text-destructive">{isoError}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="temperature" className={styles.text}>
                Temperature (°C) 
              </Label>
              <Input id="temperature" type="number" value={temperature} onChange={e => {
              setTemperature(e.target.value);
              validateTemperature(e.target.value);
            }} placeholder="e.g., 20" className={cn(styles.inputBg, styles.placeholderText, temperatureError && 'border-destructive')} onBlur={e => validateTemperature(e.target.value)} min="0" max="99" />
              {temperatureError && <p className="text-xs text-destructive">{temperatureError}</p>}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pushPull" className={styles.text}>Push/Pull Processing</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select value={pushPull} onValueChange={val => {
                setPushPull(val as 'standard' | 'push' | 'pull');
                // Revalidate stops when push/pull changes
                setTimeout(() => validatePushPullStops(pushPullStops), 0);
              }}>
                  <SelectTrigger className={cn(styles.inputBg, isDarkroomMode && "text-red-500")}>
                    <SelectValue placeholder="Standard" className={isDarkroomMode ? "text-red-500" : ""} />
                  </SelectTrigger>
                  <SelectContent className={cn(isDark ? "bg-[#2a2a2a] border-[#444]" : isDarkroomMode ? "bg-black border-red-900 text-red-500" : "")}>
                    <SelectItem value="standard" className={isDarkroomMode ? "text-red-500" : ""}>Standard</SelectItem>
                    <SelectItem value="push" className={isDarkroomMode ? "text-red-500" : ""}>Push</SelectItem>
                    <SelectItem value="pull" className={isDarkroomMode ? "text-red-500" : ""}>Pull</SelectItem>
                  </SelectContent>
                </Select>
                
                {pushPull !== 'standard' && <div className="relative">
                    <Input type="number" value={pushPullStops} onChange={e => {
                  setPushPullStops(e.target.value);
                  validatePushPullStops(e.target.value);
                }} placeholder="Stops" min="0" max="5" className={cn(styles.inputBg, styles.placeholderText, isDarkroomMode && "text-red-500", pushPullStopsError && 'border-destructive')} onBlur={e => validatePushPullStops(e.target.value)} />
                    {pushPullStopsError && <p className="text-xs text-destructive absolute">{pushPullStopsError}</p>}
                  </div>}
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes" className={cn(styles.text)}>
              Notes <span className="text-xs text-muted-foreground ml-1">
                ({notes.length}/{NOTES_MAX_LENGTH})
              </span>
            </Label>
            <Textarea id="notes" value={notes} onChange={e => {
            setNotes(e.target.value);
            validateNotes(e.target.value);
          }} placeholder="Any additional notes or observations about this recipe..." className={cn(styles.inputBg, styles.placeholderText, isDarkroomMode && "text-red-500", notesError && 'border-destructive')} maxLength={NOTES_MAX_LENGTH} onBlur={e => validateNotes(e.target.value)} />
            {notesError && <p className="text-xs text-destructive">{notesError}</p>}
          </div>
        </CardContent>
      </Card>
      
      {!isMobile && <Card className={cn(styles.cardBg, "w-full shadow-md")}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className={cn("text-lg", styles.text)}>
              Development Steps
            </CardTitle>
            
            <Button size="sm" onClick={addStep} className={cn(isDarkroomMode ? "bg-red-900 hover:bg-red-800 text-red-100" : "", isDark ? "bg-[#2a2a2a] hover:bg-[#3a3a3a] text-[#e0e0e0]" : "")}>
              <Plus className="h-4 w-4 mr-1" />
              Add Step
            </Button>
          </CardHeader>
          
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={cn(styles.tableHeaderBg)}>
                    <TableHead className={styles.text}>Step</TableHead>
                    <TableHead className={styles.text}>Time</TableHead>
                    <TableHead className={styles.text}>Agitation</TableHead>
                    <TableHead className={styles.text}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                
                <TableBody>
                  {steps.length === 0 ? <TableRow className={styles.tableRowBg}>
                      <TableCell colSpan={4} className={cn("text-center py-6", styles.text)}>
                        No steps added. Click "Add Step" to create your first development step.
                      </TableCell>
                    </TableRow> : steps.map((step, index) => <React.Fragment key={step.id}>
                        <TableRow className={cn(editingStepIndex === index && styles.editRowBg, styles.tableRowBg)}>
                          <TableCell className={styles.text}>{step.name || 'Unnamed Step'}</TableCell>
                          <TableCell className={styles.text}>{formatTime(step.time)}</TableCell>
                          <TableCell className={styles.text}>
                            {step.agitationType === 'none' ? 'None' : step.agitationType === 'continuous' ? 'Continuous' : `Every ${step.agitationInterval}s for ${step.agitationDuration}s`}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" onClick={() => editStep(index)} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "")}>
                                {editingStepIndex === index ? 'Done' : 'Edit'}
                              </Button>
                              
                              <Button size="icon" variant="ghost" onClick={() => deleteStep(index)} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "")}>
                                <Trash className="h-4 w-4" />
                              </Button>
                              
                              <Button size="icon" variant="ghost" onClick={() => moveStepUp(index)} disabled={index === 0} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "")}>
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              
                              <Button size="icon" variant="ghost" onClick={() => moveStepDown(index)} disabled={index === steps.length - 1} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "")}>
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {editingStepIndex === index && <TableRow className={cn(styles.editRowBg)}>
                            <TableCell colSpan={4}>
                              <div className="grid gap-4 p-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <div className="space-y-1">
                                    <Label htmlFor={`step-name-${index}`} className={styles.text}>
                                      Step Name
                                    </Label>
                                    <Input id={`step-name-${index}`} value={step.name} onChange={e => updateStep(index, {
                            name: e.target.value
                          })} placeholder="e.g., Developer, Stop Bath" className={styles.inputBg} />
                                  </div>
                                  
                                  <div className="space-y-1">
                                    <Label htmlFor={`step-time-${index}`} className={styles.text}>
                                      Time (in seconds)
                                    </Label>
                                    <Input id={`step-time-${index}`} type="number" value={step.time} onChange={e => updateStep(index, {
                            time: parseInt(e.target.value, 10)
                          })} min="1" className={styles.inputBg} />
                                  </div>
                                </div>
                                
                                <div className="space-y-1">
                                  <Label htmlFor={`step-agitation-${index}`} className={styles.text}>
                                    Agitation Type
                                  </Label>
                                  <Select value={step.agitationType} onValueChange={value => updateStep(index, {
                          agitationType: value as 'none' | 'continuous' | 'intermittent'
                        })}>
                                    <SelectTrigger className={styles.inputBg}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className={cn(isDark ? "bg-[#2a2a2a] border-[#444]" : isDarkroomMode ? "bg-black border-red-900" : "")}>
                                      <SelectItem value="none">None</SelectItem>
                                      <SelectItem value="continuous">Continuous</SelectItem>
                                      <SelectItem value="intermittent">Intermittent</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                {step.agitationType === 'intermittent' && <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <div className="space-y-1">
                                      <Label htmlFor={`step-interval-${index}`} className={styles.text}>
                                        Agitation Interval (seconds)
                                      </Label>
                                      <Input id={`step-interval-${index}`} type="number" value={step.agitationInterval || 60} onChange={e => updateStep(index, {
                            agitationInterval: parseInt(e.target.value, 10)
                          })} min="1" className={styles.inputBg} />
                                    </div>
                                    
                                    <div className="space-y-1">
                                      <Label htmlFor={`step-duration-${index}`} className={styles.text}>
                                        Agitation Duration (seconds)
                                      </Label>
                                      <Input id={`step-duration-${index}`} type="number" value={step.agitationDuration || 10} onChange={e => updateStep(index, {
                            agitationDuration: parseInt(e.target.value, 10)
                          })} min="1" className={styles.inputBg} />
                                    </div>
                                  </div>}
                                
                                {step.agitationType === 'continuous' && <div className="space-y-1">
                                    <Label htmlFor={`step-duration-${index}`} className={styles.text}>
                                      Agitation Duration (seconds)
                                    </Label>
                                    <Input id={`step-duration-${index}`} type="number" value={step.agitationDuration || step.time} onChange={e => updateStep(index, {
                          agitationDuration: parseInt(e.target.value, 10)
                        })} min="1" max={step.time.toString()} className={styles.inputBg} />
                                  </div>}
                                
                                <div className="space-y-1">
                                  <Label htmlFor={`step-notes-${index}`} className={styles.text}>
                                    Notes
                                  </Label>
                                  <Textarea id={`step-notes-${index}`} value={step.notes || ''} onChange={e => updateStep(index, {
                          notes: e.target.value
                        })} placeholder="Optional notes for this step..." className={styles.inputBg} />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>}
                      </React.Fragment>)}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-end">
            <Button onClick={handleSave} disabled={!name || steps.length === 0} className={cn(isDarkroomMode ? "bg-red-900 hover:bg-red-800 text-red-100" : isDark ? "bg-[#2a2a2a] hover:bg-[#3a3a3a] text-[#e0e0e0]" : "")}>
              <Save className="h-4 w-4 mr-1" />
              {isEditing ? 'Update Recipe' : 'Save Recipe'}
            </Button>
          </CardFooter>
        </Card>}
      
      {isMobile && <>
          <Card className={cn(styles.cardBg, "w-full shadow-md")}>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className={cn("text-lg", styles.text)}>
                Development Steps
              </CardTitle>
              
              <Button size="sm" onClick={openNewStepEditor} className={cn(isDarkroomMode ? "bg-red-900 hover:bg-red-800 text-red-100" : isDark ? "bg-[#2a2a2a] hover:bg-[#3a3a3a] text-[#e0e0e0]" : "")}>
                <Plus className="h-4 w-4 mr-1" />
                Add Step
              </Button>
            </CardHeader>
            
            <CardContent>
              <div className="space-y-4">
                {steps.map((step, index) => <div key={step.id} className={cn('p-3 border rounded-md', isDarkroomMode ? 'border-red-900 bg-black' : isDark ? 'border-[#333] bg-[#1e1e1e]' : 'border-border bg-card')}>
                    <div className="flex justify-between items-center">
                      <div onClick={() => openStepEditor(step)} className="flex-1">
                        <div className={cn("font-medium", styles.text)}>{index + 1}. {step.name}</div>
                        <div className={cn(styles.mutedText, "text-sm")}>
                          {Math.floor(step.time / 60)}:{String(step.time % 60).padStart(2, '0')} | 
                          {step.agitationType === 'none' ? ' No agitation' : step.agitationType === 'continuous' ? ' Continuous' : ` Every ${step.agitationInterval}s for ${step.agitationDuration}s`}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" disabled={index === 0} onClick={() => handleMoveStepUp(index)} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "", 'px-2')}>
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        
                        <Button variant="ghost" size="sm" disabled={index === steps.length - 1} onClick={() => handleMoveStepDown(index)} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "", 'px-2')}>
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                        
                        <Button variant="ghost" size="sm" onClick={() => openStepEditor(step)} className={cn(isDarkroomMode ? "text-red-500 hover:bg-red-950" : isDark ? "text-[#e0e0e0] hover:bg-[#2a2a2a]" : "")}>
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>
          
          <div className="flex justify-end pr-4 pb-4">
            <Button onClick={handleSave} disabled={!name || steps.length === 0} className={cn('w-auto', isDarkroomMode ? "bg-red-900 hover:bg-red-800 text-red-100" : isDark ? "bg-[#2a2a2a] hover:bg-[#3a3a3a] text-[#e0e0e0]" : "")}>
              <Save className="h-4 w-4 mr-1" />
              {isEditing ? 'Update Recipe' : 'Save Recipe'}
            </Button>
          </div>
        </>}
      
      <MobileStepEditor open={isEditorOpen} onOpenChange={setIsEditorOpen} step={currentStep} onSave={handleSaveStep} onDelete={isNewStep ? undefined : handleDeleteStep} onMoveUp={currentStep && !isNewStep ? handleEditorMoveStepUp : undefined} onMoveDown={currentStep && !isNewStep ? handleEditorMoveStepDown : undefined} isNew={isNewStep} isFirst={currentStepIndex === 0} isLast={currentStepIndex === steps.length - 1} darkMode={darkMode} />
    </div>;
};